import type { Candle } from '../domain/types'
import type { CalibrationBucket } from '../lib/math'

const W = 800

type Num = number | null

function extent(values: Num[]): [number, number] {
  let lo = Infinity
  let hi = -Infinity
  for (const v of values) {
    if (v === null || !Number.isFinite(v)) continue
    if (v < lo) lo = v
    if (v > hi) hi = v
  }
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return [0, 1]
  if (lo === hi) return [lo - 1, hi + 1]
  return [lo, hi]
}

function buildPath(values: Num[], xFor: (i: number) => number, yFor: (v: number) => number): string {
  let d = ''
  let pen = false
  values.forEach((v, i) => {
    if (v === null || !Number.isFinite(v)) {
      pen = false
      return
    }
    d += `${pen ? 'L' : 'M'}${xFor(i).toFixed(1)} ${yFor(v).toFixed(1)} `
    pen = true
  })
  return d.trim()
}

export interface LineSeries {
  values: Num[]
  color: string
  label?: string
  fill?: boolean
  dashed?: boolean
}

export function LineChart({
  series,
  height = 240,
  yFormat = (n: number) => n.toFixed(2),
  padY = 14,
}: {
  series: LineSeries[]
  height?: number
  yFormat?: (n: number) => string
  padY?: number
}) {
  const all: Num[] = series.flatMap((s) => s.values)
  const [lo, hi] = extent(all)
  const n = Math.max(...series.map((s) => s.values.length), 2)
  const xFor = (i: number) => (i / (n - 1)) * W
  const yFor = (v: number) => height - padY - ((v - lo) / (hi - lo)) * (height - padY * 2)
  const gridYs = [0, 0.25, 0.5, 0.75, 1]

  return (
    <svg className="chart-svg" viewBox={`0 0 ${W} ${height}`} height={height} preserveAspectRatio="none">
      {gridYs.map((g) => {
        const y = padY + g * (height - padY * 2)
        return <line key={g} x1={0} x2={W} y1={y} y2={y} stroke="var(--grid)" strokeWidth={1} />
      })}
      {series.map((s, idx) =>
        s.fill ? (
          <path
            key={`f${idx}`}
            d={`${buildPath(s.values, xFor, yFor)} L${W} ${height - padY} L0 ${height - padY} Z`}
            fill={s.color}
            opacity={0.08}
            stroke="none"
          />
        ) : null,
      )}
      {series.map((s, idx) => (
        <path
          key={idx}
          d={buildPath(s.values, xFor, yFor)}
          fill="none"
          stroke={s.color}
          strokeWidth={1.6}
          strokeDasharray={s.dashed ? '4 4' : undefined}
          vectorEffect="non-scaling-stroke"
        />
      ))}
      <text x={4} y={padY + 8} fontSize={10} fill="var(--text-faint)">
        {yFormat(hi)}
      </text>
      <text x={4} y={height - padY - 2} fontSize={10} fill="var(--text-faint)">
        {yFormat(lo)}
      </text>
    </svg>
  )
}

export function Sparkline({ values, color = 'var(--accent)', height = 40 }: { values: number[]; color?: string; height?: number }) {
  const [lo, hi] = extent(values)
  const n = Math.max(values.length, 2)
  const xFor = (i: number) => (i / (n - 1)) * W
  const yFor = (v: number) => height - 4 - ((v - lo) / (hi - lo)) * (height - 8)
  const up = values.length > 1 && values[values.length - 1] >= values[0]
  const c = color === 'auto' ? (up ? 'var(--pos)' : 'var(--neg)') : color
  return (
    <svg className="chart-svg" viewBox={`0 0 ${W} ${height}`} height={height} preserveAspectRatio="none">
      <path d={`${buildPath(values, xFor, yFor)} L${W} ${height} L0 ${height} Z`} fill={c} opacity={0.1} stroke="none" />
      <path d={buildPath(values, xFor, yFor)} fill="none" stroke={c} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
    </svg>
  )
}

export function CandleChart({
  candles,
  overlays = [],
  height = 300,
}: {
  candles: Candle[]
  overlays?: Array<{ values: Num[]; color: string; label?: string }>
  height?: number
}) {
  const view = candles.slice(-160)
  const offset = candles.length - view.length
  const highs = view.map((c) => c.high)
  const lows = view.map((c) => c.low)
  const [lo, hi] = extent([...highs, ...lows, ...overlays.flatMap((o) => o.values.slice(offset))])
  const padY = 12
  const n = Math.max(view.length, 1)
  const slot = W / n
  const bw = Math.max(1.5, slot * 0.6)
  const yFor = (v: number) => height - padY - ((v - lo) / (hi - lo)) * (height - padY * 2)

  return (
    <svg className="chart-svg" viewBox={`0 0 ${W} ${height}`} height={height} preserveAspectRatio="none">
      {[0, 0.25, 0.5, 0.75, 1].map((g) => {
        const y = padY + g * (height - padY * 2)
        return <line key={g} x1={0} x2={W} y1={y} y2={y} stroke="var(--grid)" strokeWidth={1} />
      })}
      {view.map((c, i) => {
        const x = i * slot + slot / 2
        const up = c.close >= c.open
        const color = up ? 'var(--pos)' : 'var(--neg)'
        const yO = yFor(c.open)
        const yC = yFor(c.close)
        return (
          <g key={i}>
            <line x1={x} x2={x} y1={yFor(c.high)} y2={yFor(c.low)} stroke={color} strokeWidth={1} vectorEffect="non-scaling-stroke" />
            <rect x={x - bw / 2} y={Math.min(yO, yC)} width={bw} height={Math.max(1, Math.abs(yC - yO))} fill={color} opacity={0.9} />
          </g>
        )
      })}
      {overlays.map((o, idx) => (
        <path
          key={idx}
          d={buildPath(o.values.slice(offset), (i) => i * slot + slot / 2, yFor)}
          fill="none"
          stroke={o.color}
          strokeWidth={1.4}
          vectorEffect="non-scaling-stroke"
        />
      ))}
      <text x={4} y={padY + 8} fontSize={10} fill="var(--text-faint)">
        {hi.toFixed(2)}
      </text>
      <text x={4} y={height - padY - 2} fontSize={10} fill="var(--text-faint)">
        {lo.toFixed(2)}
      </text>
    </svg>
  )
}

export function BarChart({ values, color = 'var(--info)', height = 120, baseline = 0 }: { values: Num[]; color?: string; height?: number; baseline?: number }) {
  const nums = values.map((v) => (v === null ? 0 : v))
  const [lo, hi] = extent([...nums, baseline])
  const n = Math.max(nums.length, 1)
  const slot = W / n
  const yFor = (v: number) => height - ((v - lo) / (hi - lo)) * height
  const y0 = yFor(baseline)
  return (
    <svg className="chart-svg" viewBox={`0 0 ${W} ${height}`} height={height} preserveAspectRatio="none">
      <line x1={0} x2={W} y1={y0} y2={y0} stroke="var(--border-2)" strokeWidth={1} />
      {nums.map((v, i) => {
        const y = yFor(v)
        const c = typeof color === 'string' ? (color === 'auto' ? (v >= 0 ? 'var(--pos)' : 'var(--neg)') : color) : color
        return <rect key={i} x={i * slot + slot * 0.15} width={slot * 0.7} y={Math.min(y, y0)} height={Math.max(1, Math.abs(y - y0))} fill={c} opacity={0.85} />
      })}
    </svg>
  )
}

export function CalibrationChart({ buckets, height = 240 }: { buckets: CalibrationBucket[]; height?: number }) {
  const size = height
  const pad = 24
  const xFor = (p: number) => pad + p * (size - pad * 2)
  const yFor = (p: number) => size - pad - p * (size - pad * 2)
  const maxCount = Math.max(1, ...buckets.map((b) => b.count))
  return (
    <svg className="chart-svg" viewBox={`0 0 ${size} ${size}`} height={height} style={{ maxWidth: size }}>
      <rect x={pad} y={pad} width={size - pad * 2} height={size - pad * 2} fill="none" stroke="var(--grid)" />
      <line x1={xFor(0)} y1={yFor(0)} x2={xFor(1)} y2={yFor(1)} stroke="var(--text-faint)" strokeDasharray="4 4" />
      {buckets.map((b, i) =>
        b.count > 0 ? (
          <circle
            key={i}
            cx={xFor(b.avgPredicted)}
            cy={yFor(b.observedFrequency)}
            r={3 + (b.count / maxCount) * 7}
            fill="var(--accent)"
            opacity={0.7}
          />
        ) : null,
      )}
      <text x={pad} y={size - 6} fontSize={10} fill="var(--text-faint)">
        predicted →
      </text>
      <text x={4} y={pad + 4} fontSize={10} fill="var(--text-faint)" transform={`rotate(-90 8 ${pad + 4})`}>
        observed →
      </text>
    </svg>
  )
}

export function Gauge({ value, height = 150 }: { value: number; height?: number }) {
  const v = Math.max(0, Math.min(100, value))
  const cx = 150
  const cy = 140
  const r = 110
  const polar = (frac: number) => {
    const angle = Math.PI - frac * Math.PI
    return [cx + r * Math.cos(angle), cy - r * Math.sin(angle)]
  }
  const arc = (from: number, to: number) => {
    const [x1, y1] = polar(from)
    const [x2, y2] = polar(to)
    return `M${x1.toFixed(1)} ${y1.toFixed(1)} A${r} ${r} 0 0 1 ${x2.toFixed(1)} ${y2.toFixed(1)}`
  }
  const zones: Array<[number, number, string]> = [
    [0, 0.35, 'var(--accent)'],
    [0.35, 0.6, 'var(--warn)'],
    [0.6, 0.8, '#f08a4b'],
    [0.8, 1, 'var(--danger)'],
  ]
  const [nx, ny] = polar(v / 100)
  return (
    <svg className="chart-svg" viewBox={`0 0 300 ${height + 20}`} height={height} style={{ maxWidth: 300, margin: '0 auto' }}>
      <path d={arc(0, 1)} fill="none" stroke="var(--bg-2)" strokeWidth={16} strokeLinecap="round" />
      {zones.map(([a, b, c], i) => (
        <path key={i} d={arc(a, b)} fill="none" stroke={c} strokeWidth={16} opacity={0.85} />
      ))}
      <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="var(--text)" strokeWidth={3} strokeLinecap="round" />
      <circle cx={cx} cy={cy} r={6} fill="var(--text)" />
      <text x={cx} y={cy - 18} textAnchor="middle" fontSize={30} fontFamily="var(--mono)" fill="var(--text)">
        {Math.round(v)}
      </text>
    </svg>
  )
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v))
}

function heatColor(t: number): string {
  const stops = [
    [247, 109, 109],
    [245, 177, 76],
    [54, 211, 153],
  ]
  const x = clamp01(t) * 2
  const i = Math.min(1, Math.floor(x))
  const f = x - i
  const a = stops[i]
  const b = stops[i + 1]
  const ch = (k: number) => Math.round(a[k] + (b[k] - a[k]) * f)
  return `rgb(${ch(0)},${ch(1)},${ch(2)})`
}

export interface HeatCell {
  x: number
  y: number
  metric: number
}

export function Heatmap({
  cells,
  xValues,
  yValues,
  xLabel,
  yLabel,
  height = 240,
  format = (n: number) => n.toFixed(0),
}: {
  cells: HeatCell[]
  xValues: number[]
  yValues: number[]
  xLabel?: string
  yLabel?: string
  height?: number
  format?: (n: number) => string
}) {
  const W = 520
  const padL = 46
  const padB = 28
  const padT = 10
  const padR = 10
  const gridW = W - padL - padR
  const gridH = height - padT - padB
  const cw = gridW / Math.max(1, xValues.length)
  const ch = gridH / Math.max(1, yValues.length)
  const finite = cells.map((c) => c.metric).filter((m) => Number.isFinite(m))
  const lo = finite.length ? Math.min(...finite) : 0
  const hi = finite.length ? Math.max(...finite) : 1
  const range = Math.max(1e-9, hi - lo)
  const map = new Map(cells.map((c) => [`${c.x}|${c.y}`, c.metric]))
  let best: HeatCell | null = null
  for (const c of cells) if (Number.isFinite(c.metric) && (!best || c.metric > best.metric)) best = c

  return (
    <svg className="chart-svg" viewBox={`0 0 ${W} ${height}`} height={height} style={{ maxWidth: W }}>
      {yValues.map((yv, yi) =>
        xValues.map((xv, xi) => {
          const m = map.get(`${xv}|${yv}`)
          const x = padL + xi * cw
          const y = padT + yi * ch
          const t = m !== undefined && Number.isFinite(m) ? (m - lo) / range : 0
          const isBest = best && best.x === xv && best.y === yv
          return (
            <g key={`${xi}-${yi}`}>
              <rect x={x + 1} y={y + 1} width={cw - 2} height={ch - 2} fill={m !== undefined && Number.isFinite(m) ? heatColor(t) : 'var(--bg-2)'} opacity={0.85} rx={2} />
              {isBest && <rect x={x + 1} y={y + 1} width={cw - 2} height={ch - 2} fill="none" stroke="var(--text)" strokeWidth={2} rx={2} />}
            </g>
          )
        }),
      )}
      {xValues.map((xv, xi) => (
        <text key={`xl${xi}`} x={padL + xi * cw + cw / 2} y={height - 10} fontSize={10} fill="var(--text-faint)" textAnchor="middle">
          {format(xv)}
        </text>
      ))}
      {yValues.map((yv, yi) => (
        <text key={`yl${yi}`} x={padL - 6} y={padT + yi * ch + ch / 2 + 3} fontSize={10} fill="var(--text-faint)" textAnchor="end">
          {format(yv)}
        </text>
      ))}
      {xLabel && (
        <text x={padL + gridW / 2} y={height - 22} fontSize={9} fill="var(--text-faint)" textAnchor="middle">
          {xLabel} →
        </text>
      )}
      {yLabel && (
        <text x={12} y={padT + gridH / 2} fontSize={9} fill="var(--text-faint)" textAnchor="middle" transform={`rotate(-90 12 ${padT + gridH / 2})`}>
          {yLabel} →
        </text>
      )}
    </svg>
  )
}

export interface HistMarker {
  value: number
  color: string
  label?: string
}

export function Histogram({
  values,
  bins = 24,
  markers = [],
  height = 160,
  color = 'var(--info)',
}: {
  values: number[]
  bins?: number
  markers?: HistMarker[]
  height?: number
  color?: string
}) {
  const W = 800
  if (values.length === 0) return <svg className="chart-svg" viewBox={`0 0 ${W} ${height}`} height={height} />
  let lo = Math.min(...values)
  let hi = Math.max(...values)
  for (const m of markers) {
    lo = Math.min(lo, m.value)
    hi = Math.max(hi, m.value)
  }
  if (lo === hi) {
    lo -= 1
    hi += 1
  }
  const range = hi - lo
  const counts = new Array<number>(bins).fill(0)
  for (const v of values) {
    const b = Math.min(bins - 1, Math.floor(((v - lo) / range) * bins))
    counts[b]++
  }
  const maxCount = Math.max(1, ...counts)
  const slot = W / bins
  const xOf = (v: number) => ((v - lo) / range) * W

  return (
    <svg className="chart-svg" viewBox={`0 0 ${W} ${height}`} height={height} preserveAspectRatio="none">
      {counts.map((c, i) => {
        const h = (c / maxCount) * (height - 6)
        return <rect key={i} x={i * slot + slot * 0.1} width={slot * 0.8} y={height - h} height={h} fill={color} opacity={0.7} />
      })}
      {markers.map((m, i) => (
        <line key={i} x1={xOf(m.value)} x2={xOf(m.value)} y1={0} y2={height} stroke={m.color} strokeWidth={1.5} strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
      ))}
    </svg>
  )
}

